import { Campaign, OpenInNew, Poll } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { ExternalInvitation } from '@/domain/external-invitation/entities/external-invitation.entity';
import { deleteExternalInvitationSurvey } from '@/domain/external-invitation-survey/actions/delete-external-invitation-survey.action';
import {
  selectExternalInvitationSurveyIsFetching,
  selectExternalInvitationSurveys,
  selectSurveyResultByInvitationId,
} from '@/domain/external-invitation-survey/external-invitation-survey.selectors';
import { PositionGeocode } from '@/domain/position-geocode/position-geocode.entity';
import { ProjectAbstract } from '@/domain/project/entities/project.entity';
import { selectProjectFestivalItems } from '@/domain/project-festival/project-festival.selectors';
import { selectProjectTourItems } from '@/domain/project-tour/project-tour.selectors';
import { selectProjectVenuesItems } from '@/domain/project-venue/project-venue.selectors';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';

import { ModalSurveyAnswers } from '../survey-answers/ModalSurveyAnswers';
import { ActionMenuForSurveyItem } from './ActionMenuForSurveyItem';

export function SurveyList() {
  const dispatch = useAppDispatch();
  const invitations = useAppSelector(selectExternalInvitationSurveys);
  const isFetching = useAppSelector(selectExternalInvitationSurveyIsFetching);

  const festivals = useAppSelector(selectProjectFestivalItems);
  const venues = useAppSelector(selectProjectVenuesItems);
  const tours = useAppSelector(selectProjectTourItems);
  const projects: ProjectAbstract[] = [...festivals, ...venues, ...tours];

  const [selectedInvitation, setSelectedInvitation] =
    useState<ExternalInvitation | null>(null);

  const onClickResults = (invitation: ExternalInvitation) => {
    setSelectedInvitation(invitation);
  };

  const onClickDelete = (invitation: ExternalInvitation) => {
    dispatch(deleteExternalInvitationSurvey({ invitation }));
  };

  const onCloseResultsModal = () => {
    setSelectedInvitation(null);
  };

  return (
    <Box>
      <Stack sx={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: '700' }}>
          {translate(`Surveys.list-title`, { count: invitations.length })}
        </Typography>
        {isFetching && <CircularProgress size={20} />}
      </Stack>
      <SurveyListView
        invitations={invitations}
        projects={projects}
        onClickResults={onClickResults}
        onClickDelete={onClickDelete}
      />
      {selectedInvitation && (
        <ModalSurveyAnswers
          invitation={selectedInvitation}
          open={true}
          onClose={onCloseResultsModal}
        />
      )}
    </Box>
  );
}

function SurveyListView({
  invitations,
  projects,
  onClickResults,
  onClickDelete,
}: {
  invitations: ExternalInvitation[];
  projects: ProjectAbstract[];
  onClickResults: (invitation: ExternalInvitation) => void;
  onClickDelete: (invitation: ExternalInvitation) => void;
}) {
  if (invitations.length === 0) {
    return <></>;
  }

  return (
    <TableContainer>
      <Table>
        <TableHeaderCells />
        <TableBodyCells
          externalInvitations={invitations}
          projects={projects}
          onClickResults={onClickResults}
          onClickDelete={onClickDelete}
        />
      </Table>
    </TableContainer>
  );
}

function TableHeaderCells() {
  return (
    <TableHead>
      <TableRow>
        <TableCell />
        <TableCell sx={{ color: ColorsRaw.anthracite.Anthracite_5 }}>
          {translate('Surveys.list-title')}
        </TableCell>
        <TableCell sx={{ color: ColorsRaw.anthracite.Anthracite_5 }}>
          {translate('Surveys.list-event-position')}
        </TableCell>
        <TableCell sx={{ color: ColorsRaw.anthracite.Anthracite_5 }}>
          {translate('Surveys.list-linked-project')}
        </TableCell>
        <TableCell sx={{ color: ColorsRaw.anthracite.Anthracite_5 }}>
          {translate('Surveys.list-link')}
        </TableCell>
        <TableCell sx={{ color: ColorsRaw.anthracite.Anthracite_5 }}>
          {translate('Surveys.list-answers-count')}
        </TableCell>
        <TableCell />
      </TableRow>
    </TableHead>
  );
}

function TableBodyCells({
  externalInvitations,
  projects,
  onClickResults,
  onClickDelete,
}: {
  externalInvitations: ExternalInvitation[];
  projects: ProjectAbstract[];
  onClickResults: (invitation: ExternalInvitation) => void;
  onClickDelete: (invitation: ExternalInvitation) => void;
}) {
  return (
    <TableBody>
      {externalInvitations.map((x) => (
        <SurveyRow
          key={x.id}
          invitation={x}
          projects={projects}
          onClickResults={onClickResults}
          onClickDelete={onClickDelete}
        />
      ))}
    </TableBody>
  );
}

function SurveyRow({
  invitation,
  projects,
  onClickResults,
  onClickDelete,
}: {
  invitation: ExternalInvitation;
  projects: ProjectAbstract[];
  onClickResults: (invitation: ExternalInvitation) => void;
  onClickDelete: (invitation: ExternalInvitation) => void;
}) {
  const stats = useAppSelector(selectSurveyResultByInvitationId(invitation.id));
  const totalAnswers =
    (stats?.publicMobilityTotalReplies ?? 0) +
    (stats?.publicAccommodationTotalReplies ?? 0);

  return (
    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
      <TableCell padding="checkbox">
        <Avatar sx={{ color: ColorsRaw.basic.Cream }}>
          <Campaign />
        </Avatar>
      </TableCell>
      <TableCell>
        <Typography>{invitation.title}</Typography>
      </TableCell>
      <TableCell>{formatEventPosition(invitation.eventPosition)}</TableCell>
      <TableCell>
        {formatLinkedProject(invitation.projectId, projects)}
      </TableCell>
      <TableCell padding="checkbox">
        <IconButton
          onClick={() =>
            window.open(`${invitation.scopeUrl}?code=${invitation.code}`)
          }
        >
          <Tooltip title={`${invitation.scopeUrl}?code=${invitation.code}`}>
            <OpenInNew />
          </Tooltip>
        </IconButton>
      </TableCell>
      <TableCell>
        <Chip
          sx={{ fontSize: '1rem', fontWeight: '600' }}
          label={translate('Surveys.list-answers-count-value', {
            count: totalAnswers,
          })}
        />
      </TableCell>
      <TableCell padding="checkbox">
        <Stack sx={{ flexDirection: 'row', gap: 1 }}>
          <FSPrimaryButton
            color="info"
            startIcon={<Poll />}
            onClick={() => onClickResults(invitation)}
          >
            {translate('Surveys.list-results')}
          </FSPrimaryButton>
          <ActionMenuForSurveyItem
            onClickDelete={() => onClickDelete(invitation)}
          />
        </Stack>
      </TableCell>
    </TableRow>
  );
}

function formatEventPosition(eventPosition: PositionGeocode | undefined) {
  if (!eventPosition) return '-';
  return eventPosition.properties.name;
}

function formatLinkedProject(
  invitationProjectId: number,
  projects: ProjectAbstract[],
) {
  const project = projects.find((x) => x.id === invitationProjectId);
  if (!project) return '-';
  return `${translate(`ProjectType.${project.type}`)} : ${project.name}`;
}
